import { captureException } from '@sentry/react';

import type { NotificationSignal, NotificationSignalKind } from '../types/domain.types';

export type SignalOfKind<K extends NotificationSignalKind> = Extract<NotificationSignal, { readonly kind: K }>;

export type StreamSignalHandler<K extends NotificationSignalKind> = (signal: SignalOfKind<K>) => void;

type Listener = (signal: NotificationSignal) => void;

const REPORTED_AS = { tags: { feature: 'close-the-loop-stream' } };

const signalListeners = new Map<NotificationSignalKind, Set<Listener>>();
const readyListeners = new Set<() => void>();

const isOfKind = <K extends NotificationSignalKind>(signal: NotificationSignal, kind: K): signal is SignalOfKind<K> =>
  signal.kind === kind;

const listenersOf = (kind: NotificationSignalKind): Set<Listener> => {
  const known = signalListeners.get(kind);
  if (known !== undefined) return known;
  const fresh = new Set<Listener>();
  signalListeners.set(kind, fresh);
  return fresh;
};

const runSafely = (run: () => void): void => {
  try {
    run();
  } catch (error) {
    captureException(error, REPORTED_AS);
  }
};

export const onStreamSignal = <K extends NotificationSignalKind>(kind: K, handler: StreamSignalHandler<K>): (() => void) => {
  const listeners = listenersOf(kind);
  const listener: Listener = (signal) => {
    if (isOfKind(signal, kind)) handler(signal);
  };
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const onStreamReady = (handler: () => void): (() => void) => {
  const listener = (): void => handler();
  readyListeners.add(listener);
  return () => {
    readyListeners.delete(listener);
  };
};

export const deliverStreamSignal = (signal: NotificationSignal): void => {
  const listeners = signalListeners.get(signal.kind);
  if (listeners === undefined) return;
  [...listeners].forEach((listener) => {
    if (listeners.has(listener)) runSafely(() => listener(signal));
  });
};

export const deliverStreamReady = (): void => {
  [...readyListeners].forEach((listener) => {
    if (readyListeners.has(listener)) runSafely(listener);
  });
};
